import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

const RenameGroupModal = ({ onClose, group, onRename }) => {
  const [groupName, setGroupName] = useState(group?.name || '');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!groupName.trim()) {
      toast.error('Group name cannot be empty');
      return;
    }

    if (groupName.trim() === group.name) {
      toast.info('Group name unchanged');
      onClose();
      return;
    }

    setLoading(true);
    await onRename(groupName.trim());
    setLoading(false);
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Rename Group</ModalTitle>
          <ModalCloseButton onClick={onClose}>×</ModalCloseButton>
        </ModalHeader>
        
        <ModalForm onSubmit={handleSubmit}>
          <ModalContent>
            <FormGroup>
              <FormLabel>Group Name</FormLabel>
              <FormInput
                type="text"
                placeholder="Enter new group name..."
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                maxLength={100}
                autoFocus
              />
            </FormGroup>
          </ModalContent>

          <ModalActions>
            <ModalButton type="button" variant="secondary" onClick={onClose}>
              Cancel
            </ModalButton>
            <ModalButton
              type="submit"
              variant="primary"
              disabled={loading || !groupName.trim() || groupName.trim() === group.name}
            >
              {loading ? 'Renaming...' : 'Rename'}
            </ModalButton>
          </ModalActions>
        </ModalForm>
      </ModalContainer>
    </ModalOverlay>
  );
};

// Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContainer = styled.div`
  background-color: var(--clr-bg-secondary, #2a2a2a);
  border-radius: 12px;
  width: 90vw;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--clr-border, #444);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--clr-border, #444);
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  color: var(--clr-text, #ffffff);
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  color: var(--clr-text-secondary, #aaa);
  cursor: pointer;
  font-size: 1.5rem;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background-color: var(--clr-bg-hover, #3a3a3a);
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const FormGroup = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid var(--clr-border, #444);
`;

const FormLabel = styled.label`
  display: block;
  color: var(--clr-text, #ffffff);
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 14px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px;
  background-color: var(--clr-bg-tertiary, #333);
  border: 1px solid var(--clr-border, #444);
  border-radius: 8px;
  color: var(--clr-text, #ffffff);
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: var(--clr-primary, #007bff);
  }

  &::placeholder {
    color: var(--clr-text-secondary, #aaa);
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  background-color: var(--clr-bg-tertiary, #333);
  border-top: 1px solid var(--clr-border, #444);
  flex-shrink: 0;
`;

const ModalButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background-color: var(--clr-primary, #007bff);
    color: white;

    &:hover:not(:disabled) {
      background-color: #0056b3;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  ` : `
    background-color: var(--clr-bg-secondary, #2a2a2a);
    color: var(--clr-text, #ffffff);
    border: 1px solid var(--clr-border, #444);

    &:hover {
      background-color: var(--clr-bg-hover, #3a3a3a);
    }
  `}
`;

export default RenameGroupModal;